/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { DEFAULT_LANGUAGES } from 'src/app/shared/utils/language.constants';

/**
 * Stateless param assembly for the main filter menu: turns form-control
 * selections into the exact queryParams strings and diffs URL params
 * against a preset. No side effects — control resets stay in the component.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuFilterParamsService {
  /** events, ranks, panels: plain join, null when empty/unset */
  joinSelection(values: string[] | null | undefined): string | null {
    if (!values || values.length === 0) {
      return null;
    }
    return values.join(',');
  }

  /** sentiment, lang, feed: null when everything (or nothing) is selected */
  joinUnlessAll(values: string[], totalCount: number): string | null {
    if (values.length === totalCount || values.length === 0) {
      return null;
    }
    return values.join(',');
  }

  /** categories param carries the UNSELECTED options */
  unselectedValues(
    options: { value: string }[],
    selected: string[] | null,
  ): string | null {
    if (!selected) {
      return null;
    }
    const unselected = options
      .filter((option) => !selected.includes(option.value))
      .map((option) => option.value)
      .join(',');
    return unselected || null;
  }

  /**
   * subset of a combined control belonging to one group; values like
   * 'continent eu' are reduced to their last token ('eu')
   */
  subsetParam(
    values: string[] | null | undefined,
    members: string[],
  ): string | null {
    if (!values || values.length === 0) {
      return null;
    }
    const memberSet = new Set(members);
    const subset = values.filter((item) => memberSet.has(item));
    return subset.length !== 0
      ? subset.map((item) => item.split(' ').pop()).join(',')
      : null;
  }

  geographyParams(
    values: string[] | null | undefined,
    continentValues: string[],
    countryValues: string[],
  ): { continents: string | null; countries: string | null } {
    return {
      continents: this.subsetParam(values, continentValues),
      countries: this.subsetParam(values, countryValues),
    };
  }

  /**
   * true if any URL param differs from the given preset. Timeframe is
   * deliberately excluded — it is an independent nav-bar control and is no
   * longer part of a preset.
   */
  hasFilterChanged(params: any, preset: any): boolean {
    return (
      this.hasFeedChanged(params, preset) ||
      this.hasSentimentChanged(params, preset) ||
      this.hasLanguageChanged(params, preset) ||
      this.hasRanksChanged(params, preset) ||
      this.hasPanelsChanged(params, preset) ||
      this.hasCategoriesChanged(params, preset) ||
      this.hasEventsChanged(params, preset) ||
      this.hasContinentsChanged(params, preset) ||
      this.hasCountriesChanged(params, preset) ||
      this.hasFactualityChanged(params, preset) ||
      this.hasTemporalityChanged(params, preset)
    );
  }

  private hasFeedChanged(params: any, preset: any): boolean {
    return (
      (params.feed && params.feed !== preset.feed) ||
      (!params.feed &&
        'non_professional,online,print,professional' !== preset.feed &&
        null !== preset.feed)
    );
  }

  // NOTE: preset.sentiment / preset.events / params.events mirror the legacy
  // comparisons verbatim (presets store the sentiment under `filter` and
  // events under `event_ids`) — kept bug-for-bug for zero behavior change.
  private hasSentimentChanged(params: any, preset: any): boolean {
    return (
      (params.filter && params.filter !== preset.sentiment) ||
      (!params.filter &&
        'pos,neu,neg' !== preset.sentiment &&
        null !== preset.sentiment)
    );
  }

  // The language column owns the whole `lang` value, en/de included, so compare it
  // as a whole. An absent param means DEFAULT_LANGUAGES on both sides.
  private hasLanguageChanged(params: any, preset: any): boolean {
    const resolve = (lang?: string) =>
      !lang || lang === 'multi' ? DEFAULT_LANGUAGES.join(',') : lang;
    return resolve(params.lang) !== resolve(preset.lang);
  }

  private hasRanksChanged(params: any, preset: any): boolean {
    return (
      (params.ranks && params.ranks !== preset.ranks) ||
      (!params.ranks && null !== preset.ranks)
    );
  }

  private hasPanelsChanged(params: any, preset: any): boolean {
    return (
      (params.panels && params.panels !== preset.panels) ||
      (!params.panels && null !== preset.panels)
    );
  }

  private hasCategoriesChanged(params: any, preset: any): boolean {
    return (
      (params.categories && params.categories !== preset.categories) ||
      (!params.categories && null !== preset.categories)
    );
  }

  private hasEventsChanged(params: any, preset: any): boolean {
    return (
      (params.events && params.events !== preset.events) ||
      (!params.events && null !== preset.events)
    );
  }

  private hasContinentsChanged(params: any, preset: any): boolean {
    return (
      (params.continents && params.continents !== preset.continents) ||
      (!params.continents && null !== preset.continents)
    );
  }

  private hasCountriesChanged(params: any, preset: any): boolean {
    return (
      (params.countries && params.countries !== preset.countries) ||
      (!params.countries && null !== preset.countries)
    );
  }

  private hasFactualityChanged(params: any, preset: any): boolean {
    return (
      (params.factuality && params.factuality !== preset.factuality) ||
      (!params.factuality && null !== preset.factuality)
    );
  }

  private hasTemporalityChanged(params: any, preset: any): boolean {
    return (
      (params.temporality && params.temporality !== preset.temporality) ||
      (!params.temporality && null !== preset.temporality)
    );
  }
}
